import React, { useEffect, useState } from 'react';
import AuthUser from './AuthUser';
import toast, { Toaster } from 'react-hot-toast';

export default function AssignTeacher() {
    const { http, user } = AuthUser();
    const [assignments, setAssignments] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [classes, setClasses] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        user_id: '',
        class: '',
        division: '',
    });

    useEffect(() => {
        if (user.department_id != 1) {
            toast.error("You don't have access!!");
            return;
        }
        fetchLookups();
        fetchAssignments();
    }, []);

    const fetchLookups = async () => {
        try {
            const [teacherRes, classRes, divRes] = await Promise.all([
                http.get(`/users?dept=2&login_user=${user.id}`),
                http.get(`/class?company_id=${user.company_id}`),
                http.get('/devision'),
            ]);

            const teacherList = Array.isArray(teacherRes.data)
                ? teacherRes.data
                : (teacherRes.data?.data || []);
            setTeachers(teacherList);
            setClasses(Array.isArray(classRes.data?.data) ? classRes.data.data : []);
            setDivisions(Array.isArray(divRes.data?.data) ? divRes.data.data : []);
        } catch (error) {
            console.error(error);
            toast.error('Could not load teachers / class / division');
        }
    };

    const fetchAssignments = async () => {
        setLoading(true);
        try {
            const response = await http.get(
                `/teacher_class_assigns?company_id=${user.company_id}`
            );
            if (response.data.success) {
                setAssignments(response.data.data || []);
            } else {
                toast.error(response.data.message || 'Failed to load assignments');
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error fetching assignments');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.user_id) {
            toast.error('Please select teacher');
            return;
        }
        if (!formData.class) {
            toast.error('Please select class');
            return;
        }
        if (!formData.division) {
            toast.error('Please select division');
            return;
        }

        setSaving(true);
        try {
            const response = await http.post('/teacher_class_assigns', {
                company_id: user.company_id,
                user_id: formData.user_id,
                class: formData.class,
                division: formData.division,
                login_user: user.id,
            });

            if (response.data.success) {
                toast.success(response.data.message || 'Assigned!');
                setFormData({ user_id: '', class: '', division: '' });
                fetchAssignments();
            } else {
                toast.error(response.data.message || 'Failed to assign');
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error assigning teacher');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (id) => {
        if (!window.confirm('Remove this teacher assignment?')) return;
        try {
            const response = await http.delete(
                `/teacher_class_assigns/${id}?company_id=${user.company_id}`
            );
            if (response.data.success) {
                toast.success('Assignment removed');
                fetchAssignments();
            } else {
                toast.error(response.data.message || 'Failed to remove');
            }
        } catch (error) {
            console.error(error);
            toast.error(error.response?.data?.message || 'Error removing assignment');
        }
    };

    const teacherLabel = (t) => {
        const name = `${t.name || ''} ${t.last_name || ''}`.trim();
        return name ? `${name} (${t.email || 'no email'})` : t.email || `User #${t.id}`;
    };

    const formatDate = (value) => {
        if (!value) return '—';
        try {
            const d = new Date(value);
            if (Number.isNaN(d.getTime())) return value;
            return d.toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return value;
        }
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-4 px-4">
                <div className="row mb-4">
                    <div className="col-12">
                        <h3 className="mb-1">Assign Teacher</h3>
                        <p className="text-muted mb-0">
                            Assign Team Member teachers to a class and division. Used for Notifications and Homework.
                        </p>
                    </div>
                </div>

                <div className="card mb-4">
                    <div className="card-header">
                        <h5 className="mb-0">New Assignment</h5>
                    </div>
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label">Teacher</label>
                                    <select
                                        name="user_id"
                                        className="form-control"
                                        value={formData.user_id}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Teacher</option>
                                        {teachers.map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {teacherLabel(t)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Class</label>
                                    <select
                                        name="class"
                                        className="form-control"
                                        value={formData.class}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Class</option>
                                        {classes.map((option, index) => (
                                            <option key={index} value={option.class_name}>
                                                {option.class_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Division</label>
                                    <select
                                        name="division"
                                        className="form-control"
                                        value={formData.division}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Division</option>
                                        {divisions.map((option, index) => (
                                            <option key={index} value={option.name}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-2 d-flex align-items-end">
                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100"
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Assign'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Assigned Teachers</h5>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={fetchAssignments}
                            disabled={loading}
                        >
                            <i className="fa fa-refresh me-1"></i> Refresh
                        </button>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border" role="status" />
                            </div>
                        ) : assignments.length === 0 ? (
                            <div className="text-center py-4">
                                <p className="text-muted mb-0">
                                    No teachers assigned yet. Use the form above.
                                </p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: 50 }}>#</th>
                                            <th>Teacher</th>
                                            <th>Email</th>
                                            <th>Class</th>
                                            <th>Division</th>
                                            <th>Assigned By</th>
                                            <th>Date</th>
                                            <th style={{ width: 100 }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {assignments.map((item, index) => (
                                            <tr key={item.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <strong>{item.teacher_name}</strong>
                                                </td>
                                                <td>{item.teacher_email || '—'}</td>
                                                <td>{item.class}</td>
                                                <td>{item.division}</td>
                                                <td>{item.assigned_by || '—'}</td>
                                                <td className="text-nowrap">
                                                    {formatDate(item.created_at)}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => handleRemove(item.id)}
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
